// Maps to backend/routers/financial.py
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { env } from '../utils/env';
import { showErrorNotification, showSuccessNotification } from '../utils/notificationUtils';
import { Account } from '../types/account';

// Cache for storing ETags
const etagCache = new Map<string, string>();

// Renamed from useAccountsBase to useAccounts and export directly
export function useAccounts(accessToken: string, options: { isVisible?: boolean } = {}) {
    const queryClient = useQueryClient();
    const { isVisible = true } = options;

    // The main query to fetch accounts
    const result = useQuery({
        queryKey: ['accounts'],
        queryFn: async (): Promise<Account[]> => {
            if (!accessToken) {
                return [];
            }

            try {
                const headers: Record<string, string> = {
                    'Authorization': `Bearer ${accessToken}`,
                };

                // Add If-None-Match header if we have a cached ETag
                const cachedETag = etagCache.get('accounts');
                if (cachedETag) {
                    headers['If-None-Match'] = cachedETag;
                }

                const response = await fetch(`${env.API_URL}/api/financial/accounts`, {
                    headers,
                    credentials: 'include',
                    // Add cache control headers
                    cache: 'no-cache', // Forces validation with the server
                });

                // If we get a 304 Not Modified, use the cached data
                if (response.status === 304) {
                    console.log('Using cached accounts data (304 Not Modified)');
                    return queryClient.getQueryData<Account[]>(['accounts']) || [];
                }

                // Store the new ETag for future requests
                const newETag = response.headers.get('ETag');
                if (newETag) {
                    etagCache.set('accounts', newETag);
                }

                // Check if the response is valid
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to fetch accounts');
                }

                const data = await response.json();
                return data;
            } catch (err) {
                console.error('Error fetching accounts:', err);
                throw err;
            }
        },
        enabled: isVisible, // Only fetch when component is visible
    });

    // Mutation for deleting an account
    const deleteAccountMutation = useMutation({
        mutationFn: async (itemId: string) => {
            const response = await fetch(`${env.API_URL}/api/financial/items/${itemId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error('Failed to disconnect account');
            }

            return itemId;
        },
        onSuccess: (_, itemId) => {
            showSuccessNotification('Successfully disconnected account');
            // Invalidate accounts query to refresh the data
            queryClient.invalidateQueries({ queryKey: ['accounts'] });
        },
        onError: (error) => {
            showErrorNotification(error, 'Failed to disconnect account. Please try again.');
        }
    });

    // Helper method for reconnecting accounts
    const reconnectAccount = async (itemId: string, accountSelectionEnabled: boolean = false) => {
        // Implementation will be handled in the component
        // We're just providing this as part of the hook API
        return { itemId, accountSelectionEnabled };
    };

    // Return everything needed by consumers
    return {
        ...result,
        accounts: result.data || [],
        deleteAccount: deleteAccountMutation.mutate,
        isDeleting: deleteAccountMutation.isPending,
        reconnectAccount,
    };
} 